import { useCallback, useRef } from 'react';

import { PlatformService } from '@fly/platform-service';
import {
  ChatStreamingStep,
  IChatMessagePayload,
  IChatMessageType,
  ISSMessage,
  IThread,
  StreamingStepInput,
  createSSEPostStream,
  deepSanitize,
  handleSSMessage,
  isAutoNavigateConfig,
} from '@fly/shared';
import {
  addPendingCacheInvalidation,
  chatService,
  clearError,
  clearSuggestions,
  setError,
  setIsStreaming,
  setSuggestions,
  setTargetRoute,
  setThreadId,
  useAppDispatch,
  useCreateThreadMutation,
  useLazyGetThreadMessagesQuery,
} from '@fly/store';
import { nanoid } from '@reduxjs/toolkit';
import log from 'loglevel';

const appendStreamingStep = (
  steps: ChatStreamingStep[] | undefined,
  step: StreamingStepInput
): ChatStreamingStep[] => {
  const next = [...(steps ?? [])];
  const last = next.at(-1);
  if (last?.label === step.label && last.status === step.status) {
    return next;
  }
  if (last?.status === 'active') {
    next[next.length - 1] = { ...last, status: 'done' };
  }
  next.push({ id: nanoid(), ...step });
  return next;
};

export const useChatCore = (currentThread: IThread | null) => {
  const dispatch = useAppDispatch();
  const streamRef = useRef<{ close: () => void } | null>(null);

  const [createThread] = useCreateThreadMutation();
  const [getThreadMessages] = useLazyGetThreadMessagesQuery();

  const startStream = useCallback(
    (thread: IThread, message: string, displayMessage: boolean) => {
      dispatch(clearError());

      dispatch(
        chatService.util.updateQueryData(
          'getThreadMessages',
          thread.thread_id,
          (draft) => {
            draft.push({
              type: IChatMessageType.HUMAN,
              content: deepSanitize(message.trim()),
              id: nanoid(),
            });
          }
        )
      );

      dispatch(
        chatService.util.updateQueryData(
          'getThreadMessages',
          thread.thread_id,
          (draft) => {
            draft.push({
              type: IChatMessageType.AI,
              content: '',
              id: nanoid(),
              isStreaming: true,
              streamingSteps: [],
            });
          }
        )
      );

      try {
        streamRef.current?.close();

        streamRef.current = createSSEPostStream<
          ISSMessage,
          IChatMessagePayload
        >({
          url: `${import.meta.env.VITE_APP_API_V1_URL}/wingman/runs/stream/events/`,
          payload: {
            thread_id: thread.thread_id,
            assistant_id: thread.assistant_id,
            input: {
              messages: [
                {
                  content: deepSanitize(message.trim()),
                  type: IChatMessageType.HUMAN,
                  id: nanoid(),
                },
              ],
            },
            current_page_viewed: window.location.pathname,
            auto_navigation_enabled: isAutoNavigateConfig(),
          },
          onMessage: (message) => {
            handleSSMessage(message as any, {
              onError: (errorMessage) => {
                dispatch(setError(errorMessage));
              },
              onStreamEnd: () => {
                dispatch(setIsStreaming(false));
                dispatch(
                  chatService.util.updateQueryData(
                    'getThreadMessages',
                    thread.thread_id,
                    (draft) => {
                      const last = draft.at(-1);
                      if (last) {
                        last.isStreaming = false;
                        last.streamingSteps = undefined;
                      }
                    }
                  )
                );
              },
              onStreamingStep: (step) => {
                dispatch(
                  chatService.util.updateQueryData(
                    'getThreadMessages',
                    thread.thread_id,
                    (draft) => {
                      const last = draft.at(-1);
                      if (!last) return;
                      last.isStreaming = true;
                      last.streamingSteps = appendStreamingStep(
                        last.streamingSteps,
                        step
                      );
                    }
                  )
                );
              },
              onFinalContent: (content) => {
                dispatch(
                  chatService.util.updateQueryData(
                    'getThreadMessages',
                    thread.thread_id,
                    (draft) => {
                      const last = draft.at(-1);
                      if (!last || last.content.length > 0) return;
                      last.content = content;
                      last.isStreaming = true;
                    }
                  )
                );
              },
              onContentChunk: (content) => {
                dispatch(
                  chatService.util.updateQueryData(
                    'getThreadMessages',
                    thread.thread_id,
                    (draft) => {
                      const last = draft.at(-1);
                      if (last) {
                        last.content += content;
                        last.isStreaming = true;
                      }
                    }
                  )
                );
              },
              onTitleUpdate: (title) => {
                dispatch(
                  chatService.util.updateQueryData(
                    'listThreads',
                    undefined,
                    (draft) => {
                      for (const page of draft.pages) {
                        const found = page.find(
                          (th) => th.thread_id === thread.thread_id
                        );
                        if (found) {
                          found.name = title;
                          break;
                        }
                      }
                    }
                  )
                );
                dispatch(
                  chatService.util.updateQueryData(
                    'getThread',
                    thread.thread_id,
                    (draft) => {
                      draft.name = title;
                    }
                  )
                );
              },
              onNavigation: (route) => {
                dispatch(setTargetRoute(route));
              },
              onSuggestions: (suggestions) => {
                dispatch(setSuggestions(suggestions));
              },
              onCacheInvalidation: (tags) => {
                dispatch(addPendingCacheInvalidation(tags));
              },
            });
          },
          onError: (err) => {
            log.error('SSE error:', err);
            dispatch(
              setError(
                err instanceof Error
                  ? err.message
                  : 'An error occurred while streaming'
              )
            );
          },
          onLogout: () => {
            const platformService = PlatformService.getInstance();
            platformService.logout();
          },
        });
      } catch (error) {
        log.error('Failed to create SSE stream:', error);
        dispatch(
          setError(
            error instanceof Error
              ? error.message
              : 'Failed to create SSE stream'
          )
        );
      }
    },
    [dispatch]
  );

  const sendMessage = useCallback(
    async (message: string, displayMessage = true) => {
      dispatch(clearSuggestions());
      dispatch(setIsStreaming(true));

      if (currentThread?.thread_id && currentThread?.assistant_id) {
        startStream(currentThread, message, displayMessage);
        return;
      }

      try {
        const thread = await createThread().unwrap();
        // Pre-fetch the thread messages to avoid race condition
        await getThreadMessages(thread.thread_id).unwrap();

        dispatch(setThreadId(thread.thread_id));
        startStream(thread, message, displayMessage);
      } catch (err) {
        log.error('Failed to create thread for message:', err);
        dispatch(setIsStreaming(false));
        dispatch(setError('Failed to create chat thread'));
      }
    },
    [createThread, currentThread, dispatch, getThreadMessages, startStream]
  );

  return {
    sendMessage,
    streamRef,
    currentThread,
  };
};
